import { HTMLChakraProps } from '@chakra-ui/react'
import * as React from 'react'

const dataAttr = (condition: boolean) => (condition ? '' : undefined)

export type Dimensions = {
  width: number
  height?: number
}

type Limits = {
  minWidth?: number
  maxWidth?: number
}

export type ResizeHandler = (size: Dimensions) => void

export interface ResizeOptions {
  defaultWidth?: number
  handlePosition?: 'right' | 'left'
  isResizable?: boolean
  maxWidth?: number
  minWidth?: number
  onHandleClick?: React.MouseEventHandler
  onResize?: ResizeHandler
}

export interface UseResizeProps extends ResizeOptions {}

/**
 * Hook used to create horizonally resizable elements.
 *
 * Automatically detects min/max width from css properties.
 */
export const useResize = (props: UseResizeProps = {}) => {
  const {
    defaultWidth = 280,
    onResize,
    onHandleClick,
    isResizable = true,
    handlePosition = 'right',
    minWidth,
    maxWidth,
  } = props
  const containerRef = React.useRef<HTMLDivElement>(null)
  const timeoutRef = React.useRef<any>(null)
  const [isResizing, setIsResizing] = React.useState(false)
  const [width, setWidth] = React.useState(defaultWidth)

  const dimensionsRef = React.useRef<Dimensions>({ width })
  const limitsRef = React.useRef<Limits>({ minWidth: 0, maxWidth: undefined })

  const stopResizing = React.useCallback(() => {
    setIsResizing(false)
    onResize?.(dimensionsRef.current)
  }, [])

  const resize = React.useCallback(
    (event: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return
      }

      const r = containerRef.current.getBoundingClientRect()
      const { minWidth, maxWidth } = limitsRef.current

      let w
      if (handlePosition === 'right') {
        w = event.clientX - (r?.left || 0)
      } else {
        w = r.right - event.clientX
      }

      if (w < r.width && minWidth) {
        w = Math.max(w, minWidth)
      } else if (w > r.width && maxWidth) {
        w = Math.min(w, maxWidth)
      }
      setWidth(w)

      dimensionsRef.current = { width: r.width, height: r.height }
    },
    [isResizing]
  )

  React.useEffect(() => {
    if (!containerRef.current || typeof getComputedStyle === 'undefined') {
      return
    }
    const styles = getComputedStyle(containerRef.current)
    limitsRef.current = {
      minWidth: minWidth,
      maxWidth: maxWidth || undefined,
    }
  }, [])

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', resize)
      document.addEventListener('click', stopResizing)
    }

    return () => {
      document.removeEventListener('mousemove', resize)
      document.removeEventListener('click', stopResizing)
    }
  }, [isResizing])

  // mouseup listener (replaced removed useEventListener hook)
  React.useEffect(() => {
    const onMouseUp = () => stopResizing()
    document.addEventListener('mouseup', onMouseUp)
    return () => document.removeEventListener('mouseup', onMouseUp)
  }, [stopResizing])

  const getContainerProps = React.useCallback(
    (props: HTMLChakraProps<'div'> = {}) => ({
      ref: containerRef,
      ...(isResizable
        ? {
            style: {
              width,
            } as React.CSSProperties,
          }
        : {}),
    }),
    [width, isResizable, containerRef]
  )

  const getHandleProps = React.useCallback(
    () => ({
      onMouseDown: () => {
        timeoutRef.current = setTimeout(() => setIsResizing(true), 100)
      },
      onMouseUp: (e: any) => {
        clearTimeout(timeoutRef.current)
        setIsResizing(false)
        onHandleClick?.(e)
      },
      'data-resizing': dataAttr(isResizing),
      [handlePosition]: '-5px',
    }),
    [handlePosition, isResizing]
  )

  return {
    isResizing,
    getContainerProps,
    getHandleProps,
    width,
  }
}
